import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  Stack,
  Paper,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Project import
import { gridSpacing } from 'config.js';

// Styled Components
const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(color || theme.palette.primary.main, 0.08)} 0%, ${alpha(color || theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(color || theme.palette.primary.main, 0.3)
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
  }
}));

const ViewSelfService = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [serviceData, setServiceData] = useState(null);

  // Static data
  const mockData = {
    id: parseInt(id) || 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      department: 'IT & Web Master',
      employeeId: 'EMP001',
      email: 'ahmad.rizki@eurekagroup.id',
      phone: '+62 812-3456-7890'
    },
    serviceType: 'Pengajuan Cuti',
    category: 'Leave',
    requestDate: '2024-01-10',
    startDate: '2024-01-15',
    endDate: '2024-01-17',
    totalDays: 3,
    status: 'Approved',
    approver: 'Budi Manager',
    approverPosition: 'IT Manager',
    description: 'Cuti tahunan untuk liburan keluarga. Sudah mengatur semua tugas dan proyek sebelum cuti.',
    reason: 'Liburan keluarga ke luar kota',
    attachments: [
      { name: 'Surat Keterangan.pdf', type: 'pdf' },
      { name: 'Bukti Tiket.pdf', type: 'pdf' }
    ],
    timeline: [
      {
        date: '2024-01-10',
        time: '09:00',
        action: 'Request Submitted',
        description: 'Pengajuan cuti telah dibuat',
        status: 'completed'
      },
      {
        date: '2024-01-10',
        time: '10:30',
        action: 'Under Review',
        description: 'Sedang ditinjau oleh atasan langsung',
        status: 'completed'
      },
      {
        date: '2024-01-11',
        time: '14:00',
        action: 'Approved',
        description: 'Pengajuan cuti telah disetujui oleh Budi Manager',
        status: 'completed'
      }
    ],
    notes: 'Cuti disetujui dengan catatan untuk memastikan semua tugas selesai sebelum cuti dimulai.'
  };

  useEffect(() => {
    setTimeout(() => {
      setServiceData(mockData);
      setLoading(false);
    }, 500);
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'completed':
        return theme.palette.success.main;
      case 'pending':
      case 'under review':
        return theme.palette.warning.main;
      case 'rejected':
      case 'cancelled':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Leave':
        return <CalendarIcon />;
      case 'Personal Info':
        return <PersonIcon />;
      case 'Document':
        return <DescriptionIcon />;
      default:
        return <CategoryIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!serviceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Data request tidak ditemukan</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardHeader
          avatar={
            <IconButton onClick={() => navigate('/self-service')} sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          }
          title={
            <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Detail Self-Service Request
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/self-service/edit/${id}`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Print
              </Button>
            </Stack>
          }
        />
      </Card>

      <Grid container spacing={3}>
        {/* Left Column - Employee Info & Status */}
        <Grid item xs={12} lg={4}>
          {/* Employee Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700
                  }}
                >
                  {serviceData.employee.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {serviceData.employee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {serviceData.employee.position}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {serviceData.employee.department}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{serviceData.employee.employeeId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    Request: {new Date(serviceData.requestDate).toLocaleDateString('id-ID')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CategoryIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    Kategori: {serviceData.category}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          {/* Status Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Status Request
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={serviceData.status}
                  sx={{
                    bgcolor: alpha(getStatusColor(serviceData.status), 0.1),
                    color: getStatusColor(serviceData.status),
                    border: `2px solid ${getStatusColor(serviceData.status)}`,
                    fontSize: '1rem',
                    fontWeight: 700,
                    height: 48,
                    px: 3,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                  icon={serviceData.status === 'Approved' || serviceData.status === 'Completed' ? <CheckCircleIcon /> : 
                        serviceData.status === 'Pending' ? <PendingIcon /> : <CancelIcon />}
                />
                {serviceData.approver && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Disetujui oleh: {serviceData.approver}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </InfoCard>

          {/* Service Type Card */}
          <InfoCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Tipe Layanan
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  {React.cloneElement(getCategoryIcon(serviceData.category), {
                    sx: { color: theme.palette.primary.main, fontSize: 32 }
                  })}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {serviceData.serviceType}
                </Typography>
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Right Column - Request Details */}
        <Grid item xs={12} lg={8}>
          {/* Request Information */}
          <InfoCard sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ color: 'primary.main' }} />
                  Informasi Request
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                {serviceData.startDate !== '-' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <StatCard color={theme.palette.primary.main}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Tanggal Mulai
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                            {new Date(serviceData.startDate).toLocaleDateString('id-ID', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Stack>
                      </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StatCard color={theme.palette.success.main}>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Tanggal Selesai
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                            {new Date(serviceData.endDate).toLocaleDateString('id-ID', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Stack>
                      </StatCard>
                    </Grid>
                    <Grid item xs={12}>
                      <StatCard color={theme.palette.info.main}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <AccessTimeIcon sx={{ color: theme.palette.info.main, fontSize: 32 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Total Durasi
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                              {serviceData.totalDays} Hari
                            </Typography>
                          </Box>
                        </Stack>
                      </StatCard>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Deskripsi
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {serviceData.description}
                    </Typography>
                  </Box>
                </Grid>
                {serviceData.reason && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Alasan
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {serviceData.reason}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </InfoCard>

          {/* Timeline */}
          <InfoCard sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ color: 'primary.main' }} />
                  Timeline Request
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Timeline>
                {serviceData.timeline.map((item, index) => (
                  <TimelineItem key={index}>
                    <TimelineOppositeContent sx={{ flex: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.date).toLocaleDateString('id-ID', { month: 'short', day: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.time}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: getStatusColor(item.status) }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'white' }} />
                      </TimelineDot>
                      {index < serviceData.timeline.length - 1 && (
                        <TimelineConnector sx={{ bgcolor: alpha(getStatusColor(item.status), 0.3) }} />
                      )}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.action}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </CardContent>
          </InfoCard>

          {/* Attachments & Notes */}
          <Grid container spacing={3}>
            {serviceData.attachments && serviceData.attachments.length > 0 && (
              <Grid item xs={12} md={6}>
                <InfoCard>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Lampiran
                      </Typography>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Stack spacing={1}>
                      {serviceData.attachments.map((file, index) => (
                        <Box key={index} sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}>
                          <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {file.name}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </InfoCard>
              </Grid>
            )}

            {serviceData.notes && (
              <Grid item xs={12} md={6}>
                <InfoCard>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Catatan
                      </Typography>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {serviceData.notes}
                    </Typography>
                  </CardContent>
                </InfoCard>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewSelfService;

